import React from 'react';

const fishFeatures = [
  { title: 'Feed Logging', desc: 'Track daily feed per pond, monitor FCR' },
  { title: 'Water Quality', desc: 'Log pH, DO, temperature, ammonia' },
  { title: 'Fish Seed & Sampling', desc: 'Record stocking and growth sampling' },
  { title: 'Mortality Tracking', desc: 'Log dead fish with cause and pond' },
  { title: 'Medicine Records', desc: 'Track treatments and dosages' },
  { title: 'Cycle Planning', desc: 'Plan harvests, compare cycle performance' },
];

const goatFeatures = [
  { title: 'Herd Register', desc: 'Individual goat records with breed, age, shed' },
  { title: 'Breeding & Kidding', desc: 'Track mating, pregnancy, and births' },
  { title: 'Health & Vaccination', desc: 'Schedule and log all treatments' },
  { title: 'Weight Tracking', desc: 'Monitor growth rates per animal' },
  { title: 'Feed Management', desc: 'Log feed per shed, track costs' },
  { title: 'Sale Register', desc: 'Record sales with buyer and price' },
];

const sharedFeatures = [
  { title: 'Income & Expense', desc: 'Complete financial tracking per business' },
  { title: 'Financial Reports', desc: 'Profit/loss, revenue projections, cost analysis' },
  { title: 'Inventory', desc: 'Track feed, medicine, and equipment stock' },
  { title: 'Payroll & Attendance', desc: 'Manage farm workers and payments' },
  { title: 'Task Management', desc: 'Assign and track daily farm tasks' },
  { title: 'Retail Sales', desc: 'On-farm and market sales with live monitoring' },
];

const FeatureGrid: React.FC<{ features: typeof fishFeatures; color: string }> = ({ features, color }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
    {features.map((f, i) => (
      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/60">
        <div className={`mt-0.5 w-5 h-5 rounded-full ${color} flex-shrink-0 flex items-center justify-center`}>
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{f.title}</p>
          <p className="text-gray-500 text-xs mt-0.5">{f.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

const Solution: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Everything your farm needs. One web app.
          </h2>
          <p className="mt-3 text-gray-600 text-lg">
            FarmLog is the only web platform that manages both fish ponds and goat herds together — with full financial tracking.
          </p>
        </div>

        {/* Fish features */}
        <div className="mt-14">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.893 13.393l-1.135-1.135a2.252 2.252 0 01-.421-.585l-1.08-2.16a.414.414 0 00-.663-.107.827.827 0 01-.812.21l-1.273-.363a.89.89 0 00-.738 1.595l.587.39c.59.395.674 1.23.172 1.732l-.2.2c-.212.212-.33.498-.33.796v.41c0 .409-.11.809-.32 1.158l-1.315 2.191a2.11 2.11 0 01-1.81 1.025 1.055 1.055 0 01-1.055-1.055v-1.172c0-.92-.56-1.747-1.414-2.089l-.655-.261a2.25 2.25 0 01-1.383-2.46l.007-.042a2.25 2.25 0 01.29-.787l.09-.15a2.25 2.25 0 012.37-1.048l1.178.236a1.125 1.125 0 001.302-.795l.208-.73a1.125 1.125 0 00-.578-1.315l-.665-.332-.091.091a2.25 2.25 0 01-1.591.659h-.18a.94.94 0 00-.662.274.931.931 0 01-1.458-1.137l1.411-2.353a2.25 2.25 0 00.286-.76m11.928 9.869A9 9 0 008.965 3.525m11.928 9.868A9 9 0 118.965 3.525" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Fish Farm Management</h3>
          </div>
          <div className="rounded-2xl bg-blue-50/50 p-5 border border-blue-100">
            <FeatureGrid features={fishFeatures} color="bg-blue-600" />
          </div>
        </div>

        {/* Goat features */}
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Goat Farm Management</h3>
          </div>
          <div className="rounded-2xl bg-orange-50/50 p-5 border border-orange-100">
            <FeatureGrid features={goatFeatures} color="bg-orange-600" />
          </div>
        </div>

        {/* Shared features */}
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Business & Finance</h3>
          </div>
          <div className="rounded-2xl bg-emerald-50/50 p-5 border border-emerald-100">
            <FeatureGrid features={sharedFeatures} color="bg-emerald-600" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;
